import React, { useState } from 'react';
import axios from 'axios';
import { Send, Copy, Check, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Send My Shortlist — turns the user's current compare list into a
 * public, no-login share link they can fire into WhatsApp / iMessage /
 * email.
 */
export const ShareShortlistButton = ({ propertyIds = [] }) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState(null);
  const [senderName, setSenderName] = useState('');
  const [note, setNote] = useState('');
  const [copied, setCopied] = useState(false);

  const create = async () => {
    if (!propertyIds.length) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/shortlist/share`,
        {
          property_ids: propertyIds,
          sender_name: senderName.trim() || null,
          note: note.trim() || null,
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
      setLink(res.data);
    } catch (e) {
      toast({
        variant: 'destructive',
        title: 'Could not create share link',
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!link?.url) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      toast({ title: 'Link copied' });
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Copy failed' });
    }
  };

  const whatsappHref = (() => {
    if (!link?.url) return '#';
    const txt = encodeURIComponent(
      `Have a look at this shortlist on lovinahome 🏡\n${link.url}`
    );
    return `https://wa.me/?text=${txt}`;
  })();

  const emailHref = (() => {
    if (!link?.url) return '#';
    const subj = encodeURIComponent('My lovinahome shortlist');
    const body = encodeURIComponent(
      `Here are the properties I'm looking at:\n${link.url}`
    );
    return `mailto:?subject=${subj}&body=${body}`;
  })();

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        disabled={!propertyIds.length}
        data-testid="share-shortlist-btn"
      >
        <Send className="h-4 w-4 mr-2" />
        Share shortlist
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="share-shortlist-dialog">
          <DialogHeader>
            <DialogTitle>Share your shortlist</DialogTitle>
            <DialogDescription>
              Send this list of {propertyIds.length} {propertyIds.length === 1 ? 'home' : 'homes'} to
              your partner, parents or builder — no login required for them to view.
            </DialogDescription>
          </DialogHeader>

          {!link ? (
            <div className="space-y-3 py-2">
              <div>
                <label className="text-xs font-medium text-gray-700">
                  Your name <span className="text-gray-400">(optional)</span>
                </label>
                <Input
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder="e.g. Aoife"
                  data-testid="share-shortlist-sender"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">
                  Note <span className="text-gray-400">(optional)</span>
                </label>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="What do you think of these three? I'm leaning toward #2…"
                  rows={3}
                  data-testid="share-shortlist-note"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div className="flex items-center gap-2 rounded-md border bg-gray-50 px-3 py-2">
                <code className="flex-1 text-xs truncate text-gray-700" data-testid="share-shortlist-link">
                  {link.url}
                </code>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={copy}
                  data-testid="share-shortlist-copy"
                  aria-label="Copy link"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button asChild className="bg-[#25D366] hover:bg-[#1ebe57] text-white">
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="share-shortlist-whatsapp"
                  >
                    WhatsApp
                  </a>
                </Button>
                <Button asChild variant="outline">
                  <a href={emailHref} data-testid="share-shortlist-email">Email</a>
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            {!link ? (
              <Button
                onClick={create}
                disabled={loading || !propertyIds.length}
                className="bg-[#7042cc] hover:bg-[#5b35a8] text-white"
                data-testid="share-shortlist-create"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating link…
                  </>
                ) : (
                  'Create share link'
                )}
              </Button>
            ) : (
              <Button variant="outline" onClick={() => setOpen(false)}>Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShareShortlistButton;
